<script>
import api from '@/helpers/api'
import moment from 'moment'
import { mapGetters } from 'vuex'
import { authComputed } from '@/state/helpers'
import {
  statusCounts,
  periodCounts,
  withDevCounts,
  isDashboardDev
} from './dev-data'
import QuickAdd from './quick-add.vue'

export default {
  name: 'dashboard-metrics',
  components: {
    QuickAdd
  },
  data () {
    return {
      counts: {
        month: 0,
        open: 0,
        processing: 0,
        complete: 0
      }
    }
  },
  computed: {
    ...authComputed,
    ...mapGetters('cart', ['cartCount', 'cartItems']),
    cartRoute () {
      return { name: 'cart.list' }
    },
    cartPreviewGroups () {
      const grouped = {}
      ;(this.cartItems || []).forEach(item => {
        const name = this.cartItemLabel(item)
        const qty = Number(item.quantity) || 1
        grouped[name] = (grouped[name] || 0) + qty
      })
      return Object.keys(grouped).map(name => ({
        name,
        qty: grouped[name]
      }))
    },
    cartPreviewChips () {
      return this.cartPreviewGroups.slice(0, 3)
    },
    cartPreviewExtra () {
      return Math.max(0, this.cartPreviewGroups.length - 3)
    },
    metrics () {
      const statusMetrics = [
        this.buildMetric('open', {
          label: this.$t('web_form_status.open.text'),
          route: { name: 'sales.my-orders', query: { status: 'open' } }
        }),
        this.buildMetric('processing', {
          label: this.$t('web_form_status.processing.text'),
          route: { name: 'sales.my-orders', query: { status: 'processing' } }
        }),
        this.buildMetric('complete', {
          label: this.$t('web_form_status.complete.text'),
          route: { name: 'sales.my-orders', query: { status: 'complete' } }
        })
      ]

      if (this.userRole !== 'admin') return statusMetrics

      return [
        this.buildMetric('month', {
          label: this.$t('dashboard.stats.orders_month'),
          route: { name: 'stats.products' },
          icon: 'bx bx-calendar',
          iconColor: 'text-info'
        }),
        ...statusMetrics
      ]
    }
  },
  async mounted () {
    const [month, open, processing, complete] = await Promise.all([
      this.countByPeriod(
        moment().startOf('month').toISOString(),
        moment().endOf('month').toISOString()
      ),
      this.countByStatus('open'),
      this.countByStatus('processing'),
      this.countByStatus('complete')
    ])

    this.counts = withDevCounts(
      { month, open, processing, complete },
      {
        month: periodCounts.month,
        open: statusCounts.open,
        processing: statusCounts.processing,
        complete: statusCounts.complete
      }
    )

    if (isDashboardDev()) {
      this.counts.complete = 1100 + Math.floor(Math.random() * 8900)
    }
  },
  methods: {
    buildMetric (key, { label, route, icon, iconColor }) {
      const color = iconColor
        ? null
        : this.$t(`web_form_status.${key}.color`).replace(/^soft-/, '')
      return {
        key,
        label,
        route,
        value: this.formatMetricValue(key, this.counts[key] || 0),
        icon: icon || this.$t(`web_form_status.${key}.icon`),
        iconColor: iconColor || `text-${color}`
      }
    },
    formatMetricValue (key, value) {
      if ((key !== 'complete' && key !== 'month') || value <= 1000) {
        return value
      }
      const thousands = value / 1000
      const formatted =
        thousands % 1 === 0
          ? String(thousands)
          : thousands.toFixed(1).replace(/\.0$/, '')
      return `${formatted}K`
    },
    async countByPeriod (start, end) {
      const { count } = await api.webForms.countWhere({
        filters: [
          { is_submitted: { _eq: true } },
          { submitted_at: { _gte: start, _lte: end } }
        ]
      })
      return count
    },
    async countByStatus (status) {
      const { count } = await api.webForms.countWhere({
        filters: [
          { status: { _eq: status } },
          { is_submitted: { _eq: true } }
        ]
      })
      return count
    },
    navigate (route) {
      this.$router.push(route)
    },
    cartItemLabel (item) {
      if (item.item && item.item.short_description) {
        return item.item.short_description
      }
      const type =
        item.item && item.item.webshop_custom_form
          ? item.item.webshop_custom_form
          : null
      if (type) {
        const key = `forms.${type}`
        const translated = this.$t(key)
        if (translated !== key) return translated
      }
      return this.$t('dashboard.stats.cart')
    }
  }
}
</script>

<template>
  <b-row class="mb-3">
    <b-col v-if="userRole === 'user'" cols="12" md="6" lg="3" class="mb-3 mb-lg-0">
      <quick-add variant="card" :button-label="$t('dashboard.new_order')" />
    </b-col>

    <b-col
      v-if="userRole !== 'user' || cartCount > 0"
      cols="12"
      class="order-last mt-3"
    >
      <div
        class="portal-card portal-card--metric portal-card--cart w-100 h-100"
        role="button"
        tabindex="0"
        @click="navigate(cartRoute)"
        @keyup.enter="navigate(cartRoute)"
      >
        <div class="portal-card__body">
          <div class="portal-card__main">
            <div class="portal-card__copy">
              <div class="portal-card__eyebrow">
                {{
                  $tc('dashboard.stats.cart_drafts', cartCount, {
                    count: cartCount
                  })
                }}
                · {{ $t('dashboard.stats.cart_not_submitted') }}
              </div>
              <div v-if="cartPreviewChips.length" class="portal-card__chips">
                <span
                  v-for="chip in cartPreviewChips"
                  :key="chip.name"
                  class="portal-card__chip"
                >
                  <span class="portal-card__chip-name">{{ chip.name }}</span>
                  <span class="portal-card__chip-count">{{ chip.qty }}</span>
                </span>
                <span
                  v-if="cartPreviewExtra"
                  class="portal-card__chip portal-card__chip--count-only"
                >
                  <span class="portal-card__chip-count">
                    +{{ cartPreviewExtra }}
                  </span>
                </span>
              </div>
            </div>
            <div class="portal-card__icon">
              <i
                class="bx bx-cart"
                :class="{ 'bx-tada': cartCount > 0 }"
              ></i>
            </div>
          </div>
          <div v-if="userRole === 'user'" class="portal-card__hint">
            {{ $t('dashboard.stats.cart_checkout_hint') }}
          </div>
        </div>
      </div>
    </b-col>

    <b-col
      v-for="metric in metrics"
      :key="metric.key"
      cols="12"
      md="6"
      lg="3"
      class="mb-3 mb-lg-0"
    >
      <button
        type="button"
        class="portal-card portal-card--metric w-100 h-100"
        @click="navigate(metric.route)"
      >
        <div class="portal-card__body">
          <div class="portal-card__copy">
            <div class="portal-card__eyebrow">{{ metric.label }}</div>
            <div class="portal-card__value">{{ metric.value }}</div>
          </div>
          <div class="portal-card__icon">
            <i :class="[metric.icon, metric.iconColor]"></i>
          </div>
        </div>
      </button>
    </b-col>
  </b-row>
</template>

<style lang="scss" scoped>
.portal-card {
  appearance: none;
  border: 0;
  text-align: left;
  background: #fff;
  border-radius: 1rem;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
  padding: 1.15rem 1.25rem;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 12px 28px rgba(15, 23, 42, 0.08);
  }

  &__body {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
  }

  &__eyebrow {
    color: #94a3b8;
    font-size: 0.8rem;
    font-weight: 500;
    margin-bottom: 0.35rem;
  }

  &__value {
    color: #0f172a;
    font-size: 1.85rem;
    font-weight: 700;
    line-height: 1.1;
    letter-spacing: -0.02em;
  }

  &__hint {
    margin-top: 0.55rem;
    font-size: 0.78rem;
    font-weight: 500;
    color: #64748b;
  }

  &__icon {
    flex: 0 0 auto;
    font-size: 2.25rem;
    color: #94a3b8;
    line-height: 1;
  }

  &__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    margin: 0.55rem 0 0;
  }

  &__chip {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    max-width: 16rem;
    padding: 0.18rem 0.28rem 0.18rem 0.7rem;
    border-radius: 999px;
    background: #ffffff;
    color: #1b2c3a;
    font-size: 0.75rem;
    font-weight: 600;
    box-shadow: 0 1px 4px rgba(15, 23, 42, 0.16);
  }

  &__chip-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__chip-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.35rem;
    height: 1.35rem;
    padding: 0 0.35rem;
    border-radius: 999px;
    background: #1b2c3a;
    color: #ffffff;
    font-size: 0.7rem;
    font-weight: 700;
    line-height: 1;
    flex: 0 0 auto;
  }

  &__chip--count-only {
    padding: 0.18rem 0.28rem;
  }

  &__main {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.75rem;
    width: 100%;
  }

  &--cart .portal-card__body {
    flex-direction: column;
    align-items: stretch;
  }

  &--cart {
    background: #3d5168;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
    border: 1px solid #1b2c3a;

    &:hover {
      background: #455a73;
      box-shadow: 0 12px 28px rgba(15, 23, 42, 0.18);
    }

    .portal-card__eyebrow {
      font-size: 0.95rem;
      color: #ffffff;
      margin-bottom: 0;
    }

    .portal-card__hint {
      color: #d7dcec;
    }

    .portal-card__icon {
      color: #ffffff;
    }
  }
}
</style>
